/**
 * Every link to the contact form, built in one place.
 *
 * ── Why a helper for a one-line href ─────────────────────────────────────────
 * The form has read `?interest=` since it was written, and not one of the ~15
 * CTAs on the site ever set it. So a builder who pressed "Request a trial" on
 * the pricing page arrived at a form that said "Book a demo" and had to restate
 * what they had just told us, and every row in the sales console read
 * `interest: demo` regardless of which button produced it — the one field that
 * would segment the pipeline was a constant.
 *
 * `from` is the second half of the same problem. It names the page the CTA was
 * clicked on, which is what the console needs to answer "which page is
 * actually producing enquiries" for a visitor whose first touch was lost (a
 * fresh tab, a shared link, storage disabled). It is a slug we author, never
 * the raw pathname, so it stays short and stable across URL changes.
 */

export type ContactInterest = 'demo' | 'trial' | 'pricing' | 'partnership' | 'support' | 'other';

/**
 * A link to the contact form.
 *
 * @param interest  which option the form should preselect
 * @param from      short slug for the page the CTA sits on ('pricing',
 *                  'guide-ra-bill-format', 'city-chennai', …)
 * @param lang      'ta' routes to the Tamil form. A Tamil page must never hand
 *                  its reader an English form — that was the one page of the
 *                  Tamil journey nobody had translated.
 */
export function contactHref(
  interest: ContactInterest = 'demo',
  from?: string,
  lang: 'en' | 'ta' = 'en',
): string {
  const base = lang === 'ta' ? '/ta/contact' : '/contact';
  const q = new URLSearchParams({ interest });
  if (from) q.set('from', from);
  return `${base}?${q.toString()}`;
}

/** Shorthand for the commonest case: "Book a demo" from a named page. */
export const demoHref = (from?: string, lang: 'en' | 'ta' = 'en') => contactHref('demo', from, lang);
